import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm, useFieldArray, Controller, type Control, type Path, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ChevronLeft, Loader2, Plus, Trash2, Tag, CheckCircle2,
  AlertTriangle, ShieldCheck, CreditCard, Banknote, Maximize2, Download,
} from 'lucide-react'
import { LpLayout } from './components/Layout'
import { useSalesLinkStore } from './store'
import {
  SalesLinkService,
  type SalesLinkPlan, type SalesLinkPayload,
  type LookupState, type LookupCity,
  type LookupMaritalStatus,
  type DiscountCouponPayload,
} from '@/services/modules/SalesLinkService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

// ============================================================================
// Helpers de máscara
// ============================================================================
const onlyDigits = (v: string) => (v ?? '').replace(/\D+/g, '')

function maskCpf(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2}).*/, '$1.$2.$3-$4')
}
function maskCep(v: string): string {
  const d = onlyDigits(v).slice(0, 8)
  return d.replace(/^(\d{5})(\d)/, '$1-$2')
}
function maskPhone(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 10) {
    return d
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/^\((\d{2})\) (\d{4})(\d)/, '($1) $2-$3')
  }
  return d
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/^\((\d{2})\) (\d{5})(\d)/, '($1) $2-$3')
}
function maskCardNumber(v: string): string {
  return onlyDigits(v).slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ')
}
function maskCardExpiry(v: string): string {
  const d = onlyDigits(v).slice(0, 4)
  return d.replace(/^(\d{2})(\d)/, '$1/$2')
}

/**
 * Idade em anos a partir de YYYY-MM-DD. Retorna -1 se data inválida.
 * Parse manual dos componentes (sem `new Date(iso)`) para evitar shift de fuso.
 */
function ageInYears(iso: string): number {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso ?? '').trim())
  if (!parts) return -1
  const by = +parts[1], bm = +parts[2], bd = +parts[3]
  const now = new Date()
  let age = now.getFullYear() - by
  const m = (now.getMonth() + 1) - bm
  if (m < 0 || (m === 0 && now.getDate() < bd)) age--
  return age
}

// ============================================================================
// Schema (zod) — montado a partir das regras de elegibilidade do plano
// ============================================================================
interface DepValues {
  id: string
  name: string
  cpf: string
  birthDate: string
  gender: string
  degreeRelationshipId: string
}
interface FormValues {
  name: string
  socialName: string
  rnm: string
  cpf: string
  rg: string
  birthDate: string
  gender: string
  maritalStatusId: string
  email: string
  mobile: string
  phone: string
  businessPhone: string
  postalCode: string
  address: string
  number: string
  complement: string
  district: string
  stateId: string
  cityId: string
  dueDay: string
  paymentMethod: 'BOLETO' | 'CARTÃO DE CRÉDITO'
  cardNumber: string
  cardHolderName: string
  cardExpiry: string
  cardCvv: string
  termsAccepted: boolean
  dependents: DepValues[]
}

type DegreeRule = { minAge: number | null; maxAge: number | null; degreeRelationship: string | null }

function buildSchema(params: {
  holderMin: number | null
  holderMax: number | null
  acceptsDependents: boolean
  depDegreeById: Map<string, DegreeRule>
}) {
  const { holderMin, holderMax, acceptsDependents, depDegreeById } = params

  const schema = z.object({
    name: z.string().trim().min(3, 'Informe o nome completo.'),
    socialName: z.string().optional().default(''),
    rnm: z.string().optional().default(''),
    cpf: z.string().refine((v) => onlyDigits(v).length === 11, 'CPF inválido.'),
    rg: z.string().trim().min(1, 'Informe o RG.'),
    birthDate: z.string().min(1, 'Informe a data de nascimento.'),
    gender: z.string().min(1, 'Selecione o gênero.'),
    maritalStatusId: z.string().min(1, 'Selecione o estado civil.'),
    email: z.string().trim().email('E-mail inválido.'),
    mobile: z.string().refine((v) => onlyDigits(v).length >= 10, 'Celular/WhatsApp inválido.'),
    phone: z.string().optional().default(''),
    businessPhone: z.string().optional().default(''),
    postalCode: z.string().refine((v) => onlyDigits(v).length === 8, 'CEP inválido.'),
    address: z.string().trim().min(1, 'Informe o endereço.'),
    number: z.string().trim().min(1, 'Informe o número.'),
    complement: z.string().optional().default(''),
    district: z.string().trim().min(1, 'Informe o bairro.'),
    stateId: z.string().min(1, 'Selecione o estado.'),
    cityId: z.string().min(1, 'Selecione a cidade.'),
    dueDay: z.string().min(1, 'Selecione o dia.'),
    paymentMethod: z.enum(['BOLETO', 'CARTÃO DE CRÉDITO']),
    cardNumber: z.string().optional().default(''),
    cardHolderName: z.string().optional().default(''),
    cardExpiry: z.string().optional().default(''),
    cardCvv: z.string().optional().default(''),
    termsAccepted: z.boolean().refine((v) => v === true, 'Você precisa aceitar os termos do contrato.'),
    dependents: z.array(z.object({
      id: z.string(),
      name: z.string().trim().min(3, 'Informe o nome.'),
      cpf: z.string().refine((v) => onlyDigits(v).length === 11, 'CPF inválido.'),
      birthDate: z.string().min(1, 'Informe a data de nascimento.'),
      gender: z.string().optional().default(''),
      degreeRelationshipId: z.string().min(1, 'Selecione o grau de parentesco.'),
    })).default([]),
  }).superRefine((val, ctx) => {
    // Cartão: campos obrigatórios + formato.
    if (val.paymentMethod === 'CARTÃO DE CRÉDITO') {
      if (onlyDigits(val.cardNumber).length !== 16) {
        ctx.addIssue({ code: 'custom', path: ['cardNumber'], message: 'Número do cartão inválido.' })
      }
      if (!val.cardHolderName.trim()) {
        ctx.addIssue({ code: 'custom', path: ['cardHolderName'], message: 'Informe o nome impresso no cartão.' })
      }
      if (!/^\d{2}\/\d{2}$/.test(val.cardExpiry)) {
        ctx.addIssue({ code: 'custom', path: ['cardExpiry'], message: 'Vencimento deve ser MM/AA.' })
      }
      if (!/^\d{3,4}$/.test(val.cardCvv)) {
        ctx.addIssue({ code: 'custom', path: ['cardCvv'], message: 'CVV inválido.' })
      }
    }

    // Faixa etária do titular.
    const ha = ageInYears(val.birthDate)
    if (ha >= 0 && holderMin !== null && ha < holderMin) {
      ctx.addIssue({ code: 'custom', path: ['birthDate'], message: `Titular precisa ter no mínimo ${holderMin} anos (atual: ${ha}).` })
    }
    if (ha >= 0 && holderMax !== null && ha > holderMax) {
      ctx.addIssue({ code: 'custom', path: ['birthDate'], message: `Titular precisa ter no máximo ${holderMax} anos (atual: ${ha}).` })
    }

    // Dependentes: aceitação + grau permitido + faixa etária do grau.
    if (val.dependents.length > 0 && !acceptsDependents) {
      ctx.addIssue({ code: 'custom', path: ['dependents'], message: 'Este plano não permite a inclusão de dependentes.' })
    }
    val.dependents.forEach((d, i) => {
      const rule = depDegreeById.get(d.degreeRelationshipId)
      if (d.degreeRelationshipId && !rule) {
        ctx.addIssue({ code: 'custom', path: ['dependents', i, 'degreeRelationshipId'], message: 'Grau de parentesco não permitido para este plano.' })
        return
      }
      if (rule) {
        const age = ageInYears(d.birthDate)
        if (age >= 0 && rule.minAge !== null && age < rule.minAge) {
          ctx.addIssue({ code: 'custom', path: ['dependents', i, 'birthDate'], message: `Mínimo ${rule.minAge} anos (atual: ${age}).` })
        }
        if (age >= 0 && rule.maxAge !== null && age > rule.maxAge) {
          ctx.addIssue({ code: 'custom', path: ['dependents', i, 'birthDate'], message: `Máximo ${rule.maxAge} anos (atual: ${age}).` })
        }
      }
    })
  })

  return schema
}

const DEFAULTS: FormValues = {
  name: '', socialName: '', rnm: '', cpf: '', rg: '', birthDate: '', gender: '', maritalStatusId: '',
  email: '', mobile: '', phone: '', businessPhone: '',
  postalCode: '', address: '', number: '', complement: '', district: '', stateId: '', cityId: '',
  dueDay: '5', paymentMethod: 'BOLETO',
  cardNumber: '', cardHolderName: '', cardExpiry: '', cardCvv: '',
  termsAccepted: false, dependents: [],
}

// ============================================================================
// Page (loader) — monta o form só quando o plano/elegibilidade estão prontos
// ============================================================================
export function SalesLinkSubscribePage() {
  const { slug = '', planId = '' } = useParams<{ slug: string; planId: string }>()
  const payload = useSalesLinkStore((s) => s.payload)
  const setPayload = useSalesLinkStore((s) => s.setPayload)

  const [loading, setLoading] = useState(!payload)
  const [states, setStates] = useState<LookupState[]>([])
  const [maritalStatuses, setMaritalStatuses] = useState<LookupMaritalStatus[]>([])

  useEffect(() => {
    let alive = true
    setLoading(true)
    const bootstrap = async () => {
      try {
        let data = payload
        if (!data) {
          data = await SalesLinkService.show(slug)
          if (alive) setPayload(data)
        }
        const [s, ms] = await Promise.all([
          SalesLinkService.states(),
          SalesLinkService.maritalStatuses(),
        ])
        if (!alive) return
        setStates(s)
        setMaritalStatuses(ms)
      } catch {
        toast.error('Erro ao carregar dados do plano.')
      } finally {
        if (alive) setLoading(false)
      }
    }
    bootstrap()
    return () => { alive = false }
  }, [slug, payload, setPayload])

  const plan = useMemo<SalesLinkPlan | null>(() => {
    if (!payload) return null
    return payload.plans.find((p) => p.id === planId) ?? null
  }, [payload, planId])

  if (loading) return <FullScreenSpinner />
  if (!payload || !plan) return <NotFoundScreen slug={slug} />

  return (
    <SubscribeForm
      slug={slug}
      plan={plan}
      seller={payload.seller}
      states={states}
      maritalStatuses={maritalStatuses}
    />
  )
}

// ============================================================================
// SubscribeForm — react-hook-form + zod
// ============================================================================
function SubscribeForm({ slug, plan, states, maritalStatuses }: {
  slug: string
  plan: SalesLinkPlan
  seller: SalesLinkPayload['seller']
  states: LookupState[]
  maritalStatuses: LookupMaritalStatus[]
}) {
  const navigate = useNavigate()
  const setAppliedCoupon = useSalesLinkStore((s) => s.setAppliedCoupon)
  const recordSuccess = useSalesLinkStore((s) => s.recordSuccess)

  // === Regras de elegibilidade (vindas do PRODUTO do plano) ===
  const elig = plan.eligibility ?? null
  const acceptsDependents = !!elig?.acceptsDependents
  const holderMin = elig?.holder.minAge ?? null
  const holderMax = elig?.holder.maxAge ?? null
  const depDegrees = useMemo(() => elig?.degrees ?? [], [elig])
  const depDegreeById = useMemo(
    () => new Map<string, DegreeRule>(depDegrees.map((d) => [d.degreeRelationshipId, d])),
    [depDegrees],
  )

  const schema = useMemo(
    () => buildSchema({ holderMin, holderMax, acceptsDependents, depDegreeById }),
    [holderMin, holderMax, acceptsDependents, depDegreeById],
  )

  const { control, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: DEFAULTS,
    mode: 'onTouched',
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'dependents' })

  const stateId = watch('stateId')
  const paymentMethod = watch('paymentMethod')

  // Lookups dependentes do estado
  const [cities, setCities] = useState<LookupCity[]>([])
  useEffect(() => {
    if (!stateId) { setCities([]); return }
    let alive = true
    SalesLinkService.cities(stateId)
      .then((c) => { if (alive) setCities(c) })
      .catch(() => undefined)
    return () => { alive = false }
  }, [stateId])

  // Cupom (ação, fora do RHF)
  const [couponCode, setCouponCode] = useState('')
  const [applyingCoupon, setApplyingCoupon] = useState(false)
  const [coupon, setCoupon] = useState<DiscountCouponPayload | null>(null)

  // CEP autofill
  async function handleCepBlur() {
    const cep = onlyDigits(watch('postalCode'))
    if (cep.length !== 8) return
    const loadingId = toast.loading({ title: 'Buscando CEP', description: 'Consultando ViaCEP' })
    try {
      const res = await SalesLinkService.lookupCep(cep)
      if (!res) {
        toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' })
        return
      }
      if (res.address) setValue('address', res.address, { shouldValidate: true })
      if (res.district) setValue('district', res.district, { shouldValidate: true })
      const match = states.find((s) => s.abbreviation === res.stateAbbr)
      if (match) {
        const cs = await SalesLinkService.cities(match.id)
        setCities(cs)
        setValue('stateId', match.id, { shouldValidate: true })
        const cityMatch = cs.find((c) => c.city.toLowerCase() === res.cityName.toLowerCase())
        setValue('cityId', cityMatch?.id ?? '', { shouldValidate: true })
      }
    } catch {
      toast.error({ title: 'Falha ao buscar CEP', description: 'Verifique sua conexão.' })
    } finally {
      toast.dismiss(loadingId)
    }
  }

  async function handleApplyCoupon() {
    if (!couponCode.trim()) return
    setApplyingCoupon(true)
    try {
      const c = await SalesLinkService.findDiscountCoupon(couponCode.trim(), plan.id)
      if (!c) {
        toast.error('Cupom inválido para este plano.')
        return
      }
      setCoupon(c)
      setAppliedCoupon(c)
      toast.success('Cupom aplicado!')
    } finally {
      setApplyingCoupon(false)
    }
  }
  function handleRemoveCoupon() {
    setCoupon(null)
    setCouponCode('')
    setAppliedCoupon(null)
  }

  // Pricing
  const pricing = useMemo(() => {
    const base = plan.price
    let discount = 0
    if (coupon) {
      discount = coupon.discountType === 'PORCENTAGEM' ? (base * coupon.value) / 100 : coupon.value
    }
    const total = Math.max(0, base - discount)
    return { base, discount, total: +total.toFixed(2) }
  }, [plan, coupon])

  const onSubmit = handleSubmit(async (values) => {
    try {
      const subRes = await SalesLinkService.subscribe(slug, {
        name: values.name,
        social_name: values.socialName || undefined,
        rnm: values.rnm || undefined,
        cpf: onlyDigits(values.cpf),
        rg: values.rg || undefined,
        birth_date: values.birthDate,
        gender: (values.gender || undefined) as never,
        marital_status_id: values.maritalStatusId || undefined,
        phone: onlyDigits(values.phone) || undefined,
        mobile: onlyDigits(values.mobile),
        business_phone: onlyDigits(values.businessPhone) || undefined,
        email: values.email,
        postal_code: onlyDigits(values.postalCode),
        address: values.address,
        number: values.number,
        complement: values.complement || undefined,
        district: values.district,
        city_id: values.cityId,
        plan_id: plan.id,
        due_day: Number(values.dueDay),
        payment_method: values.paymentMethod,
        discount_coupon_id: coupon?.id,
        dependents: values.dependents.map((d) => ({
          name: d.name,
          cpf: onlyDigits(d.cpf),
          birth_date: d.birthDate,
          gender: (d.gender || undefined) as never,
          degree_relationship_id: d.degreeRelationshipId || undefined,
        })),
        terms_accepted: true,
      })

      const payRes = await SalesLinkService.doPayment(slug, {
        subscriber_id: subRes.subscriberId,
        payment_method: values.paymentMethod,
        amount: pricing.total,
        card: values.paymentMethod === 'CARTÃO DE CRÉDITO' ? {
          holder_name: values.cardHolderName,
          number: onlyDigits(values.cardNumber),
          expiry: values.cardExpiry,
          cvv: values.cardCvv,
        } : undefined,
      })

      recordSuccess(plan.name, payRes.amountPaid, payRes.method, payRes.bankSlipUrl)
      navigate('/vendas/sucesso')
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data
      if (msg?.errors) {
        const first = Object.values(msg.errors)[0]?.[0]
        toast.error(first ?? msg.message ?? 'Falha ao concluir contratação.')
      } else {
        toast.error(msg?.message ?? 'Falha ao concluir contratação.')
      }
    }
  }, () => {
    toast.error({ title: 'Revise o formulário', description: 'Há campos obrigatórios ou inválidos destacados em vermelho.' })
  })

  return (
    <LpLayout>
      <form onSubmit={onSubmit} className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link
          to={`/vendas/${slug}`}
          className="cursor-pointer inline-flex items-center gap-1.5 text-sm text-[#56758d] hover:text-[#243c4f] mb-6"
        >
          <ChevronLeft size={14} /> VOLTAR
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* ====== FORM ====== */}
          <div className="space-y-6 order-2 lg:order-1">
            <SectionCard title="Dados pessoais">
              <Row>
                <TextField control={control} name="name" label="Nome Completo" required col="sm:col-span-2" placeholder="Maria da Silva" />
                <TextField control={control} name="socialName" label="Nome Social" placeholder="Como prefere ser chamado(a)" />
                <TextField control={control} name="rnm" label="RNM" placeholder="Registro Nacional Migratório" />
                <TextField control={control} name="cpf" label="CPF" required mask={maskCpf} inputMode="numeric" placeholder="000.000.000-00" />
                <TextField control={control} name="rg" label="RG" required placeholder="00.000.000-0" />
                <TextField control={control} name="birthDate" label="Data de Nascimento" required type="date" />
                <SelectField control={control} name="gender" label="Gênero" required options={[
                  { value: '', label: 'Selecione o gênero' },
                  { value: 'MASCULINO', label: 'Masculino' },
                  { value: 'FEMININO', label: 'Feminino' },
                  { value: 'OUTRO', label: 'Outro' },
                ]} />
                <SelectField control={control} name="maritalStatusId" label="Estado Civil" required options={[
                  { value: '', label: 'Selecione o estado civil' },
                  ...maritalStatuses.map((m) => ({ value: m.id, label: m.description })),
                ]} />
              </Row>
            </SectionCard>

            <SectionCard title="Contato">
              <Row>
                <TextField control={control} name="email" label="Email" required col="sm:col-span-2" type="email" placeholder="voce@exemplo.com" />
                <TextField control={control} name="mobile" label="Celular/WhatsApp" required mask={maskPhone} inputMode="tel" placeholder="(00) 00000-0000" />
                <TextField control={control} name="phone" label="Telefone Residencial" mask={maskPhone} inputMode="tel" placeholder="(00) 0000-0000" />
                <TextField control={control} name="businessPhone" label="Telefone Comercial" mask={maskPhone} inputMode="tel" placeholder="(00) 0000-0000" />
              </Row>
            </SectionCard>

            <SectionCard title="Endereço">
              <Row>
                <TextField control={control} name="postalCode" label="CEP" required mask={maskCep} inputMode="numeric" placeholder="00000-000" onBlurExtra={handleCepBlur} />
                <TextField control={control} name="address" label="Endereço" required col="sm:col-span-2" placeholder="Rua, avenida, etc." />
                <TextField control={control} name="number" label="Número" required placeholder="Nº" />
                <TextField control={control} name="complement" label="Complemento" placeholder="Apto, bloco, sala" />
                <TextField control={control} name="district" label="Bairro" required placeholder="Bairro" />
                {/* Estado — ao trocar, limpa a cidade */}
                <Controller control={control} name="stateId" render={({ field, fieldState }) => (
                  <Field label="Estado" required>
                    <Select
                      value={field.value}
                      onChange={(v) => { field.onChange(v); setValue('cityId', '', { shouldValidate: false }) }}
                      error={fieldState.error?.message}
                      options={[{ value: '', label: 'Selecione o estado' }, ...states.map((s) => ({ value: s.id, label: s.state }))]}
                    />
                  </Field>
                )} />
                <SelectField control={control} name="cityId" label="Cidade" required disabled={!stateId} options={[
                  { value: '', label: stateId ? 'Selecione a cidade' : 'Escolha um estado antes' },
                  ...cities.map((c) => ({ value: c.id, label: c.city })),
                ]} />
              </Row>
            </SectionCard>

            {/* Dependentes — só aparece se o plano aceitar dependentes */}
            {acceptsDependents && (
              <SectionCard
                title="Dependentes"
                action={
                  <button type="button"
                    onClick={() => append({ id: crypto.randomUUID(), name: '', cpf: '', birthDate: '', gender: '', degreeRelationshipId: '' })}
                    className="cursor-pointer inline-flex items-center gap-1 text-sm font-medium text-[#f07d00] hover:underline">
                    <Plus size={14} /> Adicionar dependente
                  </button>
                }
              >
                {depDegrees.length > 0 && (
                  <div className="mb-4 rounded-md bg-blue-50 ring-1 ring-blue-200 px-3 py-2 text-[13px] text-blue-900">
                    <strong>Dependentes aceitos:</strong>{' '}
                    {depDegrees.map((d) => {
                      const range = d.minAge !== null || d.maxAge !== null
                        ? ` (${d.minAge ?? 0}–${d.maxAge ?? '∞'} anos)` : ''
                      return `${d.degreeRelationship}${range}`
                    }).join(', ')}.
                  </div>
                )}

                {typeof errors.dependents?.message === 'string' && (
                  <p className="mb-3 text-[12px] text-rose-500">{errors.dependents.message}</p>
                )}

                {fields.length === 0 ? (
                  <p className="text-sm text-[#56758d]">
                    Você ainda não adicionou nenhum dependente. Clique em <strong>Adicionar dependente</strong> para incluir os familiares aceitos pelo plano.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {fields.map((f, idx) => (
                      <div key={f.id} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-[#243c4f]">Dependente {idx + 1}</div>
                          <button type="button" onClick={() => remove(idx)} className="cursor-pointer text-xs text-rose-600 hover:underline inline-flex items-center gap-1">
                            <Trash2 size={12} /> Remover
                          </button>
                        </div>
                        <Row>
                          <TextField control={control} name={`dependents.${idx}.name`} label="Nome Completo" required col="sm:col-span-2" placeholder="Nome do dependente" />
                          <TextField control={control} name={`dependents.${idx}.cpf`} label="CPF" required mask={maskCpf} inputMode="numeric" placeholder="000.000.000-00" />
                          <TextField control={control} name={`dependents.${idx}.birthDate`} label="Data de Nascimento" required type="date" />
                          <SelectField control={control} name={`dependents.${idx}.gender`} label="Gênero" options={[
                            { value: '', label: 'Selecione' },
                            { value: 'MASCULINO', label: 'Masculino' },
                            { value: 'FEMININO', label: 'Feminino' },
                            { value: 'OUTRO', label: 'Outro' },
                          ]} />
                          <SelectField control={control} name={`dependents.${idx}.degreeRelationshipId`} label="Grau de parentesco" required options={[
                            { value: '', label: 'Selecione' },
                            ...depDegrees.map((g) => ({ value: g.degreeRelationshipId, label: g.degreeRelationship ?? '—' })),
                          ]} />
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            <SectionCard title="Pagamento">
              <Row>
                <SelectField control={control} name="dueDay" label="Dia de pagamento" required options={[5, 10, 15, 20, 25, 30].map((d) => ({ value: String(d), label: `Dia ${d}` }))} />
                <Controller control={control} name="paymentMethod" render={({ field }) => (
                  <Field label="Método de pagamento" required col="sm:col-span-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(['BOLETO', 'CARTÃO DE CRÉDITO'] as const).map((m) => (
                        <PaymentChoice key={m} method={m} active={field.value === m} onClick={() => field.onChange(m)} />
                      ))}
                    </div>
                  </Field>
                )} />
              </Row>

              {paymentMethod === 'CARTÃO DE CRÉDITO' && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Row>
                    <TextField control={control} name="cardNumber" label="Número do cartão" required col="sm:col-span-2" mask={maskCardNumber} inputMode="numeric" placeholder="0000 0000 0000 0000" />
                    <TextField control={control} name="cardHolderName" label="Nome impresso no cartão" required col="sm:col-span-2" mask={(v) => v.toUpperCase()} placeholder="NOME COMO ESTÁ NO CARTÃO" />
                    <TextField control={control} name="cardExpiry" label="Vencimento (MM/AA)" required mask={maskCardExpiry} inputMode="numeric" placeholder="MM/AA" />
                    <TextField control={control} name="cardCvv" label="CVV" required mask={(v) => onlyDigits(v).slice(0, 4)} inputMode="numeric" placeholder="123" />
                  </Row>
                  <p className="text-[11px] text-[#56758d] mt-3 inline-flex items-start gap-1">
                    <ShieldCheck size={12} className="mt-0.5 shrink-0 text-emerald-500" />
                    Seus dados de cartão são enviados diretamente ao gateway de pagamento (Asaas) e não são armazenados em nosso sistema.
                  </p>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Cupom de desconto">
              {coupon ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg p-4">
                  <div className="flex items-start gap-2 text-emerald-700">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                    <span className="text-sm">
                      Cupom <strong>{coupon.code}</strong> aplicado —{' '}
                      {coupon.discountType === 'PORCENTAGEM'
                        ? `${coupon.value}% de desconto`
                        : `R$ ${coupon.value.toFixed(2).replace('.', ',')} de desconto`}
                      <br />
                      <span className="text-emerald-800/80">
                        Valor original R$ {pricing.base.toFixed(2).replace('.', ',')} − desconto R$ {pricing.discount.toFixed(2).replace('.', ',')} = <strong>R$ {pricing.total.toFixed(2).replace('.', ',')}</strong>
                      </span>
                    </span>
                  </div>
                  <button type="button" onClick={handleRemoveCoupon} className="cursor-pointer text-sm text-rose-600 hover:underline shrink-0">
                    Remover cupom
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative">
                    <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#56758d]" />
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Código do cupom de desconto"
                      autoComplete="off"
                      className="w-full h-12 pl-9 pr-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#046fb7]/30 focus:border-[#046fb7] uppercase"
                    />
                  </div>
                  <button type="button" onClick={handleApplyCoupon} disabled={!couponCode.trim() || applyingCoupon}
                    className="cursor-pointer h-12 px-5 rounded-lg bg-[#243C4F] hover:bg-[#035f9e] text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed">
                    {applyingCoupon ? <Loader2 size={14} className="animate-spin" /> : 'Aplicar cupom'}
                  </button>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Contrato de Adesão">
              {plan.lpContractUrl ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm text-[#56758d]">Leia o contrato de adesão antes de finalizar.</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={plan.lpContractUrl} target="_blank" rel="noreferrer" className="cursor-pointer inline-flex items-center gap-1 text-sm font-medium text-[#046fb7] hover:underline">
                        <Maximize2 size={14} /> Expandir
                      </a>
                      <a href={plan.lpContractUrl} download className="cursor-pointer inline-flex items-center gap-1 text-sm font-medium text-[#046fb7] hover:underline">
                        <Download size={14} /> Baixar
                      </a>
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-200 overflow-hidden mb-4 bg-gray-50">
                    <iframe title="Contrato de adesão" src={plan.lpContractUrl} className="w-full h-72" />
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#56758d] mb-3">
                  O contrato de adesão será enviado por e-mail após a contratação.
                </p>
              )}
              <Controller control={control} name="termsAccepted" render={({ field, fieldState }) => (
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#f07d00]"
                    />
                    <span className="text-sm text-[#243c4f]">
                      Li e concordo com os termos do contrato de adesão <span className="text-rose-500">*</span>
                    </span>
                  </label>
                  {fieldState.error?.message && (
                    <p className="text-[12px] text-rose-500 mt-1">{fieldState.error.message}</p>
                  )}
                </div>
              )} />
            </SectionCard>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="cursor-pointer inline-flex items-center justify-center gap-2 h-14 px-8 rounded-lg bg-[#f07d00] hover:bg-[#e67300] text-white text-base font-bold transition disabled:opacity-50 disabled:cursor-not-allowed min-w-[260px]"
              >
                {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                CONTRATAR
              </button>
            </div>
          </div>

          {/* ====== RESUMO ====== */}
          <aside className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-5 sticky top-4">
              <div className="text-[11px] uppercase tracking-wider text-[#f07d00] font-bold mb-1">PLANO</div>
              <h3 className="text-[20px] font-bold text-[#243c4f] leading-tight">{plan.name}</h3>
              <p className="text-[12px] text-[#56758d] mt-1">{plan.planType}</p>

              <div className="mt-5 pt-4 border-t border-gray-100 space-y-2 text-sm">
                <div className="flex justify-between text-[#56758d]">
                  <span>Valor do plano</span>
                  <span>R$ {pricing.base.toFixed(2).replace('.', ',')}</span>
                </div>
                {coupon && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Desconto ({coupon.code})</span>
                    <span>− R$ {pricing.discount.toFixed(2).replace('.', ',')}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-gray-100">
                  <span className="text-sm text-[#243c4f]">Total</span>
                  <span className="text-2xl font-bold text-[#243c4f]">
                    R$ {pricing.total.toFixed(2).replace('.', ',')}
                    <span className="text-xs text-[#f07d00] font-bold ml-1">/MÊS</span>
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-[11px] text-[#56758d]">
                <div className="flex items-center gap-2"><CreditCard size={14} className="text-emerald-500" /> Pagamento mensal recorrente</div>
              </div>
            </div>
          </aside>
        </div>
      </form>
    </LpLayout>
  )
}

// ============================================================================
// Subcomponentes auxiliares
// ============================================================================
function SectionCard({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[#243c4f] text-lg font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
}

function Field({ label, col, required, children }: { label: string; col?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className={col}>
      <label className="block text-[12px] text-[#56758d] mb-1.5">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  )
}

/** Campo de texto controlado por RHF (com máscara opcional + erro inline). */
function TextField({ control, name, label, required, col, mask, placeholder, inputMode, type, onBlurExtra }: {
  control: Control<FormValues>
  name: Path<FormValues>
  label: string
  required?: boolean
  col?: string
  mask?: (v: string) => string
  placeholder?: string
  inputMode?: 'numeric' | 'tel' | 'email' | 'text'
  type?: string
  onBlurExtra?: () => void
}) {
  return (
    <Controller control={control} name={name} render={({ field, fieldState }) => (
      <Field label={label} required={required} col={col}>
        <Input
          type={type}
          value={String(field.value ?? '')}
          inputMode={inputMode}
          placeholder={placeholder}
          onChange={(v) => field.onChange(mask ? mask(v) : v)}
          onBlur={() => { field.onBlur(); onBlurExtra?.() }}
          error={fieldState.error?.message}
        />
      </Field>
    )} />
  )
}

/** Select controlado por RHF (com erro inline). */
function SelectField({ control, name, label, required, col, options, disabled }: {
  control: Control<FormValues>
  name: Path<FormValues>
  label: string
  required?: boolean
  col?: string
  options: { value: string; label: string }[]
  disabled?: boolean
}) {
  return (
    <Controller control={control} name={name} render={({ field, fieldState }) => (
      <Field label={label} required={required} col={col}>
        <Select
          value={String(field.value ?? '')}
          onChange={field.onChange}
          options={options}
          disabled={disabled}
          error={fieldState.error?.message}
        />
      </Field>
    )} />
  )
}

interface InputBaseProps {
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  placeholder?: string
  type?: string
  inputMode?: 'numeric' | 'tel' | 'email' | 'text'
  error?: string
}
function Input({ value, onChange, onBlur, placeholder, type = 'text', inputMode, error }: InputBaseProps) {
  return (
    <>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete="off"
        aria-invalid={error ? true : undefined}
        className={cn(
          'w-full h-11 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 bg-white text-[#243c4f]',
          error
            ? 'border-rose-400 focus:ring-rose-200 focus:border-rose-400'
            : 'border-gray-300 focus:ring-[#046fb7]/30 focus:border-[#046fb7]',
        )}
      />
      {error && <p className="text-[12px] text-rose-500 mt-1">{error}</p>}
    </>
  )
}

interface SelectBaseProps {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  disabled?: boolean
  error?: string
}
function Select({ value, onChange, options, disabled, error }: SelectBaseProps) {
  return (
    <>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={error ? true : undefined}
        className={cn(
          'w-full h-11 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 bg-white text-[#243c4f] disabled:bg-gray-50 disabled:text-gray-400',
          error
            ? 'border-rose-400 focus:ring-rose-200 focus:border-rose-400'
            : 'border-gray-300 focus:ring-[#046fb7]/30 focus:border-[#046fb7]',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && <p className="text-[12px] text-rose-500 mt-1">{error}</p>}
    </>
  )
}

function PaymentChoice({ method, active, onClick }: { method: 'BOLETO' | 'CARTÃO DE CRÉDITO'; active: boolean; onClick: () => void }) {
  const Icon = method === 'CARTÃO DE CRÉDITO' ? CreditCard : Banknote
  const label = method === 'CARTÃO DE CRÉDITO' ? 'Cartão de crédito' : 'Boleto'
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'cursor-pointer flex items-center gap-2 px-3 py-3 rounded-lg ring-1 transition text-sm font-medium',
        active ? 'ring-[#046fb7] bg-[#046fb7]/5 text-[#243c4f]' : 'ring-gray-200 text-[#56758d] hover:bg-gray-50',
      )}
    >
      <Icon size={16} className={active ? 'text-[#046fb7]' : 'text-[#56758d]'} />
      {label}
    </button>
  )
}

// ============================================================================
// Loading / NotFound
// ============================================================================
function FullScreenSpinner() {
  return (
    <div className="lp-root min-h-dvh flex items-center justify-center bg-gray-50">
      <Loader2 size={28} className="animate-spin text-[#56758d]" />
    </div>
  )
}

function NotFoundScreen({ slug }: { slug: string }) {
  return (
    <div className="lp-root min-h-dvh flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md bg-white rounded-2xl border border-gray-200 p-8 shadow-card">
        <AlertTriangle size={36} className="mx-auto text-[#f07d00] mb-3" />
        <h1 className="text-xl font-bold text-[#243c4f]">Plano não encontrado</h1>
        <p className="text-sm text-[#56758d] mt-2">Volte para a lista de planos e tente novamente.</p>
        <Link to={`/vendas/${slug}`} className="cursor-pointer inline-block mt-4 text-sm text-[#046fb7] font-medium hover:underline">
          Voltar aos planos
        </Link>
      </div>
    </div>
  )
}
